import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Sparkles, Lock, Heart, MessageCircle, LayoutDashboard } from 'lucide-react';
import { Button } from '@/shared/components/ui/button';
import { useAutoLoginFromQuery } from '@/features/authentication/hooks/useAutoLoginFromQuery';
import { useGuestSignupModal } from '../context/GuestSignupModalContext';
import { useUnauthRumAction } from '../hooks/useUnauthRumAction';

type OrientationCard = {
  tag: string;
  title: string;
  body: string;
  /** Public route. Mutually exclusive with `gated`. */
  href?: string;
  /** Gated feature — clicking opens the signup modal. */
  gated?: boolean;
  accent: string;
};

const ORIENTATION_CARDS: OrientationCard[] = [
  {
    tag: 'Navigator',
    title: "Tell us about your situation and we'll help identify the right experts to support you.",
    body: "Whether you're navigating aging care, a health event, end-of-life planning, or loss, Navigator helps you get oriented and take the next step.",
    href: '/care-navigator',
    accent: 'bg-[#378F94]',
  },
  {
    tag: 'Verified Providers',
    title: 'Know the expert you need?',
    body: 'Browse our ecosystem of vetted professionals across aging care, caregiving, end-of-life support, after-loss services, and more. Filter by specialty, location, and service type to find trusted support.',
    href: '/search-providers',
    accent: 'bg-[#ECE9E7]',
  },
  {
    tag: 'Resource Hub',
    title: 'Looking for information?',
    body: 'Explore articles, guides, webinars, podcasts, and planning tools designed to help families make informed decisions with confidence. Practical guidance for aging, caregiving, end-of-life planning, and life after loss.',
    href: '/resources',
    accent: 'bg-[#B6C0BF]',
  },
];

const LOCKED_FEATURES = [
  {
    icon: Heart,
    title: 'Saved Resources',
    body: 'Build your list of trusted providers and information resources',
  },
  {
    icon: MessageCircle,
    title: 'Connect Directly',
    body: 'Message or schedule free consultations in-platform with providers',
  },
  {
    icon: LayoutDashboard,
    title: 'Personal Dashboard',
    body: 'Track care requests and recommended considerations.',
  },
];

function OrientationCardItem({ card }: { card: OrientationCard }) {
  const { openModal } = useGuestSignupModal();
  const trackAction = useUnauthRumAction();
  const className = `${card.accent} rounded-xl p-6 flex flex-col gap-3 hover:opacity-90 transition-opacity group text-left`;
  const content = (
    <>
      <div className="flex items-center gap-2">
        <span className="text-xs font-termina font-bold uppercase text-black tracking-wide">
          {card.tag}
        </span>
      </div>
      <h3 className="text-lg font-semibold text-black leading-snug">{card.title}</h3>
      <p className="text-sm text-black leading-relaxed flex-1">{card.body}</p>
      <span className="text-black font-bold text-sm group-hover:translate-x-1 transition-transform inline-block">
        →
      </span>
    </>
  );

  if (card.gated) {
    return (
      <button
        type="button"
        onClick={() => {
          trackAction('unauth.locked_tab_click', { tab: card.tag });
          openModal({ trigger: 'locked_tab_click', triggerEntityId: card.tag });
        }}
        className={className}
      >
        {content}
      </button>
    );
  }

  return (
    <Link to={card.href!} className={className}>
      {content}
    </Link>
  );
}

function LockedFeatureCard({ icon: Icon, title, body }: (typeof LOCKED_FEATURES)[0]) {
  return (
    <div className="bg-white border border-gray-100 rounded-xl p-5 flex flex-col gap-2 relative overflow-hidden">
      <div className="absolute top-3 right-3 bg-gray-100 rounded-full p-1">
        <Lock className="w-3.5 h-3.5 text-gray-400" />
      </div>
      <div className="w-8 h-8 bg-[#f0faf7] rounded-lg flex items-center justify-center">
        <Icon className="w-4 h-4 text-[#026857]" />
      </div>
      <strong className="text-sm font-semibold text-gray-700">{title}</strong>
      <span className="text-xs text-gray-400">{body}</span>
    </div>
  );
}

export const PreLoginLanding = () => {
  const { openModal } = useGuestSignupModal();
  const trackAction = useUnauthRumAction();
  const landingViewFiredRef = useRef(false);

  // Picks up `?autoLogin=true&email=X` redirects from legacy signup pages
  // (RegisterPage, PartnerSignupPage, InviteSuccessState) and kicks off the
  // Kinde OTP flow without an extra click. No-op when the params are absent.
  useAutoLoginFromQuery();

  // `trackAction` itself has stable identity (ref-backed inside the hook), so
  // this effect runs once per mount. The ref guard is here to protect against
  // React StrictMode's intentional double-mount in dev and any future
  // remounts (e.g. layout switching) — without it we'd emit two
  // `unauth.landing_view` events for a single user landing.
  useEffect(() => {
    if (landingViewFiredRef.current) return;
    landingViewFiredRef.current = true;
    trackAction('unauth.landing_view');
  }, [trackAction]);

  return (
    <div className="min-h-screen bg-background">
      {/* Hero */}
      <section className="container mx-auto px-4 pt-16 pb-12 max-w-4xl">
        <p className="text-xs font-termina font-bold uppercase tracking-widest text-[#026857] mb-3">
          Welcome to bQuest
        </p>
        <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 leading-tight mb-5">
          The right support,<br className="hidden sm:block" /> when life gets complicated.
        </h1>
        <p className="text-lg text-gray-600 leading-relaxed max-w-2xl mb-8">
          Connect with trusted experts and resources to help you navigate aging, caregiving, end of
          life, and loss with confidence.
        </p>
        <div className="flex flex-wrap gap-3 mb-8">
          <Button asChild className="bg-[#026857] text-white hover:bg-[#025246] font-bold gap-2">
            <Link
              to="/care-navigator"
              onClick={() => trackAction('unauth.navigator_click', { source: 'hero_cta' })}
            >
              <Sparkles className="w-4 h-4" />
              Find the Right Expert
            </Link>
          </Button>
          <Button
            asChild
            variant="outline"
            className="border-[#026857] text-[#026857] hover:bg-[#026857]/5 font-bold"
          >
            <Link to="/search-providers">Browse verified providers</Link>
          </Button>
        </div>
        <div className="flex flex-wrap items-center gap-2 text-sm text-gray-500">
          <span>Verified providers across the US</span>
          <span className="text-gray-300">·</span>
          <span><strong className="text-gray-700">1,200+</strong> articles &amp; guides</span>
          <span className="text-gray-300">·</span>
          <span>Trusted by leading financial advisors</span>
        </div>
      </section>

      {/* Orientation cards */}
      <section className="container mx-auto px-4 pb-14 max-w-4xl">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {ORIENTATION_CARDS.map((card) => (
            <OrientationCardItem key={card.tag} card={card} />
          ))}
        </div>
      </section>

      {/* Sign up to unlock */}
      <section className="bg-white border-t border-gray-100 py-14">
        <div className="container mx-auto px-4 max-w-4xl">
          <h2 className="text-2xl font-semibold text-gray-900 mb-8">Sign up to unlock the rest.</h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
            {LOCKED_FEATURES.map((feature) => (
              <LockedFeatureCard key={feature.title} {...feature} />
            ))}
          </div>
          <Button
            type="button"
            onClick={() => {
              trackAction('unauth.banner_click');
              openModal({ trigger: 'banner_cta' });
            }}
            className="bg-[#026857] text-white hover:bg-[#025246] font-bold"
          >
            Get full access- it's free
          </Button>
          <p className="text-xs text-gray-400 mt-3">No credit card required. Takes about 30 seconds.</p>
        </div>
      </section>
    </div>
  );
};
